import { useState } from "react";
import { useActivityReport, type ActivityReportRequest } from "../api/reports";
import { useCampaigns } from "../api/campaigns";
import { useTeams } from "../api/teams";
import { useUsers } from "../api/users";
import "./activity-reports-page.css";

const ACTIVITY_TYPES = [
    { value: "", label: "Alla typer" },
    { value: "DoorKnock", label: "Dörrknackning" },
    { value: "Handout", label: "Utdelning" },
    { value: "Conversation", label: "Samtal" },
    { value: "Poster", label: "Affisch" },
];

export const ActivityReportsPage = (): JSX.Element => {
    const [filters, setFilters] = useState<ActivityReportRequest>({});
    const [sortBy, setSortBy] = useState<"activities" | "type" | "campaign">("activities");
    const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

    const { data: reportData = [], isLoading } = useActivityReport(filters);
    const { data: campaigns = [] } = useCampaigns();
    const { data: teams = [] } = useTeams();
    const { data: users = [] } = useUsers();

    const handleFilterChange = (key: keyof ActivityReportRequest, value: string | undefined) => {
        setFilters((prev) => ({
            ...prev,
            [key]: value || undefined,
        }));
    };

    const handleExport = () => {
        const params = new URLSearchParams();
        if (filters.teamId) params.append("teamId", filters.teamId);
        if (filters.campaignId) params.append("campaignId", filters.campaignId);
        if (filters.type) params.append("type", filters.type);
        if (filters.from) params.append("from", filters.from);
        if (filters.to) params.append("to", filters.to);
        if (filters.userId) params.append("userId", filters.userId);

        // Create CSV content
        const headers = ["Kampanj", "Team", "Typ", "Antal aktiviteter", "Kvantitet"];
        const rows = reportData.map((row) => [
            campaigns.find((c) => c.id === row.campaignId)?.name || row.campaignId,
            teams.find((t) => t.id === row.teamId)?.name || row.teamId,
            ACTIVITY_TYPES.find((t) => t.value === row.type)?.label || row.type,
            row.activities.toString(),
            (row.quantitySum || 0).toString(),
        ]);

        const csvContent = [headers, ...rows]
            .map((row) => row.map((cell) => `"${cell}"`).join(","))
            .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `activity-reports-${new Date().toISOString().split("T")[0]}.csv`;
        link.click();
    };

    const sortedData = [...reportData].sort((a, b) => {
        let comparison = 0;
        switch (sortBy) {
            case "activities":
                comparison = a.activities - b.activities;
                break;
            case "type":
                comparison = a.type.localeCompare(b.type);
                break;
            case "campaign":
                comparison = a.campaignId.localeCompare(b.campaignId);
                break;
        }
        return sortOrder === "asc" ? comparison : -comparison;
    });

    const totalActivities = reportData.reduce((sum, row) => sum + row.activities, 0);
    const totalQuantity = reportData.reduce((sum, row) => sum + (row.quantitySum || 0), 0);
    const uniqueTypes = new Set(reportData.map((row) => row.type)).size;

    return (
        <section className="activity-reports-page" aria-label="Aktivitetsrapporter">
            <div className="activity-reports-page__header">
                <h2>Aktivitetsrapporter</h2>
                <div className="activity-reports-page__actions">
                    <button
                        className="activity-reports-page__export-button"
                        onClick={handleExport}
                        disabled={reportData.length === 0}
                    >
                        Exportera CSV
                    </button>
                </div>
            </div>

            <div className="activity-reports-page__filters">
                <div className="filter-group">
                    <label htmlFor="filter-team">Team:</label>
                    <select
                        id="filter-team"
                        value={filters.teamId || ""}
                        onChange={(e) => handleFilterChange("teamId", e.target.value)}
                        className="filter-select"
                    >
                        <option value="">Alla teams</option>
                        {teams.map((team) => (
                            <option key={team.id} value={team.id}>
                                {team.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label htmlFor="filter-campaign">Kampanj:</label>
                    <select
                        id="filter-campaign"
                        value={filters.campaignId || ""}
                        onChange={(e) => handleFilterChange("campaignId", e.target.value)}
                        className="filter-select"
                    >
                        <option value="">Alla kampanjer</option>
                        {campaigns.map((campaign) => (
                            <option key={campaign.id} value={campaign.id}>
                                {campaign.name}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label htmlFor="filter-type">Aktivitetstyp:</label>
                    <select
                        id="filter-type"
                        value={filters.type || ""}
                        onChange={(e) => handleFilterChange("type", e.target.value)}
                        className="filter-select"
                    >
                        {ACTIVITY_TYPES.map((type) => (
                            <option key={type.value} value={type.value}>
                                {type.label}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label htmlFor="filter-user">Användare:</label>
                    <select
                        id="filter-user"
                        value={filters.userId || ""}
                        onChange={(e) => handleFilterChange("userId", e.target.value)}
                        className="filter-select"
                    >
                        <option value="">Alla användare</option>
                        {users.map((user) => (
                            <option key={user.id} value={user.id}>
                                {user.userName}
                            </option>
                        ))}
                    </select>
                </div>

                <div className="filter-group">
                    <label htmlFor="filter-from">Från:</label>
                    <input
                        id="filter-from"
                        type="date"
                        value={filters.from?.split("T")[0] || ""}
                        onChange={(e) =>
                            handleFilterChange("from", e.target.value ? `${e.target.value}T00:00:00Z` : undefined)
                        }
                        className="filter-input"
                    />
                </div>

                <div className="filter-group">
                    <label htmlFor="filter-to">Till:</label>
                    <input
                        id="filter-to"
                        type="date"
                        value={filters.to?.split("T")[0] || ""}
                        onChange={(e) =>
                            handleFilterChange("to", e.target.value ? `${e.target.value}T23:59:59Z` : undefined)
                        }
                        className="filter-input"
                    />
                </div>

                <div className="filter-group">
                    <button
                        className="filter-clear-button"
                        onClick={() => setFilters({})}
                    >
                        Rensa filter
                    </button>
                </div>
            </div>

            <div className="activity-reports-page__summary">
                <div className="summary-card">
                    <p className="summary-card__label">Totalt antal aktiviteter</p>
                    <p className="summary-card__value">{totalActivities.toLocaleString()}</p>
                </div>
                <div className="summary-card">
                    <p className="summary-card__label">Totalt kvantitet</p>
                    <p className="summary-card__value">{totalQuantity.toLocaleString()}</p>
                </div>
                <div className="summary-card">
                    <p className="summary-card__label">Antal aktivitetstyper</p>
                    <p className="summary-card__value">{uniqueTypes}</p>
                </div>
            </div>

            {isLoading ? (
                <div className="activity-reports-page__empty">
                    <p>Laddar...</p>
                </div>
            ) : sortedData.length === 0 ? (
                <div className="activity-reports-page__empty">
                    <p>Inga aktiviteter hittades med de valda filtren.</p>
                </div>
            ) : (
                <div className="activity-reports-page__table-container">
                    <table className="activity-reports-table">
                        <thead>
                            <tr>
                                <th>
                                    <button
                                        className="table-sort-button"
                                        onClick={() => {
                                            setSortBy("campaign");
                                            setSortOrder(sortBy === "campaign" && sortOrder === "asc" ? "desc" : "asc");
                                        }}
                                    >
                                        Kampanj {sortBy === "campaign" && (sortOrder === "asc" ? "↑" : "↓")}
                                    </button>
                                </th>
                                <th>Team</th>
                                <th>
                                    <button
                                        className="table-sort-button"
                                        onClick={() => {
                                            setSortBy("type");
                                            setSortOrder(sortBy === "type" && sortOrder === "asc" ? "desc" : "asc");
                                        }}
                                    >
                                        Typ {sortBy === "type" && (sortOrder === "asc" ? "↑" : "↓")}
                                    </button>
                                </th>
                                <th>
                                    <button
                                        className="table-sort-button"
                                        onClick={() => {
                                            setSortBy("activities");
                                            setSortOrder(
                                                sortBy === "activities" && sortOrder === "asc" ? "desc" : "asc"
                                            );
                                        }}
                                    >
                                        Antal aktiviteter {sortBy === "activities" && (sortOrder === "asc" ? "↑" : "↓")}
                                    </button>
                                </th>
                                <th>Kvantitet</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedData.map((row, index) => (
                                <tr key={`${row.campaignId}-${row.type}-${index}`}>
                                    <td>
                                        {campaigns.find((c) => c.id === row.campaignId)?.name || row.campaignId}
                                    </td>
                                    <td>{teams.find((t) => t.id === row.teamId)?.name || row.teamId}</td>
                                    <td>
                                        {ACTIVITY_TYPES.find((t) => t.value === row.type)?.label || row.type}
                                    </td>
                                    <td>{row.activities.toLocaleString()}</td>
                                    <td>{(row.quantitySum || 0).toLocaleString()}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </section>
    );
};